import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography
} from "@material-ui/core";
import Label, { LabelColor } from "../../Label";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  WhatsApp as WhatsAppIcon
} from "@material-ui/icons";
import { PATH_DASHBOARD } from "../../../routes/paths";
import {
  archiveDocument,
  canEditSentDocument,
  displayDocumentStatus,
  downloadOrgPreparedPdfBlob,
  downloadOrgSignedPdfBlob,
  downloadSignedPdfBlob,
  downloadPreparedPdfBlob,
  getOrgSignatureDocument,
  isSigningLinkLive,
  subscribeToDocument,
  type OrgSignatureDocumentView,
  type SignatureDocumentDisplayStatus
} from "../../../services/signatures";
import { buildNewSignatureParams } from "./GlobalNewSignatureDialog";
import {
  SignatureDocument,
  SignatureSignerStatus,
  SignatureWhatsappDeliveryStatus
} from "../../../@types/signatures";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import SigningLinkWhatsappShareButton from "./SigningLinkWhatsappShare";
import WhatsappConfirmDialog from "./WhatsappConfirmDialog";
import { sendSigningLinkFor, sentConfirmation } from "../../../services/signingLinkDelivery";
import { deliveryRecipientLabel } from "../../../utils/signatureWhatsappShare";
import ContactWidgetDialog from "../common/ContactWidgetDialog";
import { computeContactDisplay } from "../common/useContactDisplay";
import useContactsLookup from "../../../hooks/contacts/useContactsLookup";
import ListingPreviewModal from "../common/ListingPreviewModal";
import ListingChip from "./ListingChip";
import { formatPhoneLocal } from "../../../services/signatureFormatters";
import { getDocumentFileUrl } from "../../../services/signPublic";
import {
  questionnaireLabelContext,
  signatureDocumentDisplayName
} from "../../../utils/signatureQuestionnaireName";
import {
  questionnaireInMessage,
  signerQuestionnaireStep,
  type QuestionnaireStepDocument
} from "../../../utils/signatureQuestionnaireStep";
import QuestionnaireStepLabel, { QUESTIONNAIRE_STEP_COLOR } from "./QuestionnaireStepLabel";
import type { ListingListedFor } from "../../../@types/sharedSchema";
import { isQuestionnaireOnlyDocument } from "../../../@types/sharedSchema";
import ForwardSendIcon from "../../ForwardSendIcon";

const documentStatusColor = (status: SignatureDocumentDisplayStatus): LabelColor => {
  switch (status) {
    case "draft":
    case "created":
      return "default";
    case "sent":
      return "info";
    case "partial":
      return "warning";
    case "completed":
      return "success";
    case "declined":
      return "error";
    case "expired":
      return "secondary";
    default:
      return "default";
  }
};

const signerStatusColor = (s: SignatureSignerStatus): LabelColor => {
  switch (s) {
    case "pending":
      return "default";
    case "sent":
      return "primary";
    case "viewed":
      return "info";
    case "signed":
      return "success";
    case "declined":
      return "error";
    default:
      return "default";
  }
};

const whatsappStatusColor = (s: SignatureWhatsappDeliveryStatus): LabelColor => {
  switch (s) {
    case "sent":
      return "success";
    case "failed":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

// Format any string/Date/Timestamp to an "DD/MM/YYYY · HH:mm" locale-aware
// display string. Returns "—" for empty values so the UI always has a
// consistent token to render.
const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Small 2-column row used throughout the panel for "label · value" pairs.
// Keeps the metadata blocks visually consistent and makes long identifier
// values (ids, message ids) wrap gracefully without breaking the layout.
function MetaRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 110, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          wordBreak: "break-all",
          fontFamily: mono ? "monospace" : undefined
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Stacking level for every dialog this panel opens. The panel itself renders
 * as a fixed overlay at zIndex 2001 on the documents list and the contact /
 * listing signature tabs, which is ABOVE MUI's default modal level (1300) —
 * so any dialog spawned from inside it must be lifted past 2001 or it opens
 * behind the panel. ListingPreviewModal hard-codes the same 2100.
 */
const PANEL_DIALOG_Z_INDEX = 2100;

type DocumentDetailPanelProps = {
  documentId: string | null;
  onClose?: () => void;
  onArchived?: () => void;
  /**
   * A COLLEAGUE's document, opened by an office admin from the "כל המשרד" list.
   * The panel then loads once through the office endpoint, renders from the
   * redacted projection and offers nothing that sends, edits, shares or
   * archives. Forgetting this prop on a colleague's document yields an error,
   * never data: the Firestore rule refuses the owner-mode listener.
   */
  readOnly?: boolean;
  /**
   * Who the document belongs to, for the read-only notice. A PROP on purpose:
   * the panel is mounted by four hosts and must not grow a dependency on the
   * org directory or the session. Falls back to the owner's email.
   */
  ownerLabel?: string;
  /** Read-only: the document is gone for this viewer (archived, or its owner left the office). */
  onNotFound?: () => void;
  /**
   * The other contracts of this document's SET, as the host has them (the sent list): an automatic
   * send from here is the set's one message, and the confirmation names the contract of the set that
   * brings the owner questionnaire. Absent: the confirmation reads this document alone.
   */
  setDocuments?: SignatureDocument[];
};

/**
 * The post-signing tasks a document carries (in ceremony order): the
 * addresses confirmation for hidden-addresses sends, and the statutory seller
 * questionnaire. Single source of truth for the per-signer task-status block
 * and the interim-download label below.
 */
type PostSignTask = "addendum" | "questionnaire";
const postSignTasksOf = (doc: {
  hideAddresses?: boolean;
  hasSellerQuestionnaire: boolean;
}): PostSignTask[] => [
  ...(doc.hideAddresses ? (["addendum"] as PostSignTask[]) : []),
  ...(doc.hasSellerQuestionnaire ? (["questionnaire"] as PostSignTask[]) : [])
];
const postSignTaskLabelKey: Record<PostSignTask, string> = {
  addendum: "signatures.detail.taskAddendum",
  questionnaire: "signatures.detail.taskQuestionnaire"
};

/** The dates the questionnaire step reads, from either shape's signer — nothing else of it. */
const questionnaireStepSigner = (s: {
  status?: string;
  mainSignedAt?: string;
  addendumSignedAt?: string;
  questionnaireSignedAt?: string;
}) => ({
  status: s.status,
  mainSignedAt: s.mainSignedAt,
  addendumSignedAt: s.addendumSignedAt,
  questionnaireSignedAt: s.questionnaireSignedAt
});

/**
 * ── What the panel renders from ─────────────────────────────────────────────
 *
 * The panel shows two different shapes: the owner's own `SignatureDocument`
 * (live, with signing links and storage URLs) and a colleague's
 * `OrgSignatureDocumentView` (one-shot, an allowlist, `has*` flags instead of
 * URLs). Every field and every action the panel draws comes from this
 * view-model, so "what may a viewer do with this document" is decided in
 * exactly two places: `ownerPanelView` and `officePanelView`.
 *
 * That is deliberate. The office shape was never cast to `SignatureDocument`
 * and gated with `readOnly &&` checks on each button, because the cast
 * compiles and then every existing gate (`signedDocument?.url`, `sentPdfUrl`,
 * `sellerQuestionnaire`) reads `undefined` — a completed colleague document
 * would open with no download at all, silently.
 */

/** A file served as a plain link (`href`), or fetched as a blob through the office endpoint (`null`). */
type PanelFile = { href: string | null };

type PanelSignerView = {
  id: string;
  displayName: string;
  /** "" when the signer has no phone. */
  displayPhone: string;
  email?: string;
  /** What the signer block hands to the contact dialog; absent = nothing to open. */
  contact?: { phone: string; name?: string };
  status: SignatureSignerStatus;
  sentAt?: string;
  viewedAt?: string;
  mainSignedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  otpVerifiedAt?: string;
  otpSkippedInPerson: boolean;
  otpSkippedInPersonAt?: string | Date;
  addendumSignedAt?: string;
  questionnaireSignedAt?: string;
  whatsapp: {
    status: SignatureWhatsappDeliveryStatus;
    attemptCount: number;
    lastAttemptAt?: string;
    /** Owner only: the provider's message id and error text stay with the owner. */
    messageId?: string;
    error?: string;
  };
  /** Owner only. A signing link is a credential to sign as the customer. */
  signingUrl: string | null;
  canSignInPerson: boolean;
  canResend: boolean;
  /** Owner only: "שיתוף בוואטסאפ" — the realtor's own chat, pre-filled with this signer's link. */
  canShareOnWhatsapp: boolean;
};

type PanelDocumentView = {
  id: string;
  formattedNumber: string;
  templateNameSnapshot: string;
  /** What the document is called: the template's name, or a questionnaire-only document's own. */
  displayName: string;
  /**
   * A questionnaire-only document (שאלון בלבד): the questionnaire IS the document — no post-signing
   * task, one file (the signed PDF is the questionnaire), worded as a questionnaire.
   */
  questionnaireOnly: boolean;
  templateId: string;
  displayStatus: SignatureDocumentDisplayStatus;
  createdTime?: string;
  sentAt?: string;
  completedAt?: string;
  updatedTime?: string;
  listingIds: string[];
  contactIds: string[];
  otpRequired?: boolean;
  notes?: string;
  signers: PanelSignerView[];
  postSignTasks: PostSignTask[];
  /** Names the questionnaire: a rental's is שאלון בעל זכות בנכס. */
  questionnaireListedFor?: ListingListedFor;
  /**
   * What the questionnaire chip beside the status reads (`QuestionnaireStepLabel`, the lists' own
   * rule): copied field by field from either shape, so nothing else of the document rides along.
   */
  questionnaireStep: QuestionnaireStepDocument;
  /** Signer blocks open the caller's own contact — only ever for the caller's own document. */
  canOpenContacts: boolean;
  canEdit: boolean;
  canShare: boolean;
  /** "הדפסה להחתמה ידנית": the prepared PDF, downloaded for a wet signature. */
  canPrintForSigning: boolean;
  /** "צפייה במסמך שנשלח": the same PDF, only to look at. */
  canViewPrepared: boolean;
  /** The signed PDF in the inline preview dialog. */
  previewUrl: string | null;
  signedPdf: PanelFile | null;
  questionnairePdf: PanelFile | null;
  canDownloadAudit: boolean;
  canDownloadInterim: boolean;
  canArchive: boolean;
};

const ownerPanelView = (
  doc: SignatureDocument,
  lookup: Parameters<typeof computeContactDisplay>[1]
): PanelDocumentView => {
  const hasSignedPdf = !!doc.signedDocument?.url && !!doc.id;
  const inPersonOpen =
    !doc.archivedAt && ["sent", "partial"].includes(doc.status) && doc.signingEngine === "native";
  // A link worth handing out: the document still awaits signatures and was not archived.
  const shareOpen = !doc.archivedAt && ["sent", "partial"].includes(doc.status);
  const questionnaireOnly = isQuestionnaireOnlyDocument(doc);
  return {
    id: doc.id,
    formattedNumber: doc.formattedNumber,
    templateNameSnapshot: doc.templateNameSnapshot,
    displayName: signatureDocumentDisplayName(doc),
    questionnaireOnly,
    templateId: doc.templateId,
    displayStatus: displayDocumentStatus(doc),
    createdTime: doc.createdTime,
    sentAt: doc.sentAt,
    completedAt: doc.completedAt,
    updatedTime: doc.updatedTime,
    listingIds: doc.listingIds,
    contactIds: doc.contactIds,
    otpRequired: doc.otpRequired,
    notes: doc.notes,
    signers: doc.signers.map((s) => {
      // Resolve every signer's display through computeContactDisplay
      // so a matching CRM contact's name takes precedence over the
      // signer-name snapshot stored on the document. Pure function,
      // safe to call inside .map(). When no contact matches — or the
      // lookup has not answered yet — falls back to the signer's
      // stored name.
      const display = computeContactDisplay(s.phone, lookup, s.name);
      const signingUrl = s.signing?.publicSigningUrl || null;
      return {
        id: s.id,
        displayName: display.contactName || s.name || display.displayPhone,
        displayPhone: s.phone ? display.displayPhone || formatPhoneLocal(s.phone) : "",
        email: s.email,
        contact: s.phone ? { phone: s.phone, name: display.contactName || s.name } : undefined,
        status: s.status,
        sentAt: s.sentAt,
        viewedAt: s.viewedAt,
        mainSignedAt: s.mainSignedAt,
        signedAt: s.signedAt,
        declinedAt: s.declinedAt,
        otpVerifiedAt: s.otpVerifiedAt,
        otpSkippedInPerson: !!s.inPersonOtpSkip,
        otpSkippedInPersonAt: s.inPersonOtpSkip?.at,
        addendumSignedAt: s.addendumSignedAt,
        questionnaireSignedAt: s.questionnaireSignedAt,
        whatsapp: {
          status: s.whatsappDelivery.status,
          attemptCount: s.whatsappDelivery.attemptCount,
          lastAttemptAt: s.whatsappDelivery.lastAttemptAt,
          messageId: s.whatsappDelivery.messageId,
          error: s.whatsappDelivery.error
        },
        signingUrl,
        canSignInPerson:
          inPersonOpen && s.status !== "signed" && s.status !== "declined" && !!signingUrl,
        canResend: s.status !== "signed" && !!s.signing?.slug,
        canShareOnWhatsapp:
          shareOpen && s.status !== "signed" && s.status !== "declined" && !!signingUrl
      };
    }),
    postSignTasks: questionnaireOnly
      ? []
      : postSignTasksOf({
          hideAddresses: doc.hideAddresses,
          hasSellerQuestionnaire: !!doc.sellerQuestionnaire
        }),
    questionnaireListedFor: doc.sellerQuestionnaire?.listedFor,
    questionnaireStep: {
      status: doc.status,
      documentKind: doc.documentKind,
      hideAddresses: doc.hideAddresses,
      hasSellerQuestionnaire: !!doc.sellerQuestionnaire,
      sellerQuestionnaireListedFor: doc.sellerQuestionnaire?.listedFor,
      signers: doc.signers.map(questionnaireStepSigner)
    },
    canOpenContacts: true,
    canEdit: canEditSentDocument(doc),
    canShare: isSigningLinkLive(doc) && !!doc.signers[0]?.signing?.publicSigningUrl,
    canPrintForSigning: !!doc.sentPdfUrl && doc.status !== "draft" && !doc.archivedAt,
    canViewPrepared: false,
    // Signed PDF is only available once the signer has completed. Serve it through
    // our own streaming endpoint (never the raw storage.googleapis.com URL).
    previewUrl: hasSignedPdf ? getDocumentFileUrl(doc.id, { type: "signed" }) : null,
    signedPdf: hasSignedPdf
      ? { href: getDocumentFileUrl(doc.id, { type: "signed", download: true }) }
      : null,
    questionnairePdf:
      doc.sellerQuestionnaireFile?.url && doc.id && !questionnaireOnly
        ? { href: getDocumentFileUrl(doc.id, { type: "questionnaire", download: true }) }
        : null,
    canDownloadAudit: !!doc.signedDocument?.auditLogUrl && !!doc.id,
    canDownloadInterim: !doc.signedDocument?.url && doc.status === "partial" && !!doc.id,
    canArchive: true
  };
};

/**
 * A colleague's document. Reads the projection's own fields and NOTHING else,
 * field by field — so even a payload carrying more than the allowlist (a
 * signing link, a storage URL, the provider's error text) cannot put any of
 * it on screen. Every file is fetched through the authenticated office
 * endpoint; `getDocumentFileUrl` (the public by-id stream) is never called
 * here: it runs no access check and writes no audit line.
 */
const officePanelView = (doc: OrgSignatureDocumentView): PanelDocumentView => ({
  id: doc.id,
  formattedNumber: doc.formattedNumber,
  templateNameSnapshot: doc.templateNameSnapshot,
  displayName: signatureDocumentDisplayName(doc),
  questionnaireOnly: isQuestionnaireOnlyDocument(doc),
  templateId: doc.templateId,
  displayStatus: displayDocumentStatus(doc),
  createdTime: doc.createdTime,
  sentAt: doc.sentAt,
  completedAt: doc.completedAt,
  updatedTime: doc.updatedTime,
  listingIds: doc.listingIds ?? [],
  contactIds: doc.contactIds ?? [],
  otpRequired: doc.otpRequired,
  // Named from the DOCUMENT. The viewer's own contact book says nothing about a
  // colleague's customer, and would print the viewer's name for that phone.
  signers: (doc.signers ?? []).map((s) => {
    const phone = s.phone ? formatPhoneLocal(s.phone) : "";
    return {
      id: s.id,
      displayName: s.name || phone,
      displayPhone: phone,
      email: s.email,
      status: s.status,
      sentAt: s.sentAt,
      viewedAt: s.viewedAt,
      mainSignedAt: s.mainSignedAt,
      signedAt: s.signedAt,
      declinedAt: s.declinedAt,
      otpVerifiedAt: s.otpVerifiedAt,
      otpSkippedInPerson: !!s.inPersonOtpSkip,
      otpSkippedInPersonAt: s.inPersonOtpSkip?.at,
      addendumSignedAt: s.addendumSignedAt,
      questionnaireSignedAt: s.questionnaireSignedAt,
      whatsapp: {
        status: s.whatsappDelivery.status,
        attemptCount: s.whatsappDelivery.attemptCount,
        lastAttemptAt: s.whatsappDelivery.lastAttemptAt
      },
      signingUrl: null,
      canSignInPerson: false,
      canResend: false,
      canShareOnWhatsapp: false
    };
  }),
  postSignTasks: isQuestionnaireOnlyDocument(doc) ? [] : postSignTasksOf(doc),
  questionnaireListedFor: doc.sellerQuestionnaireListedFor,
  questionnaireStep: {
    status: doc.status,
    documentKind: doc.documentKind,
    hideAddresses: doc.hideAddresses,
    hasSellerQuestionnaire: doc.hasSellerQuestionnaire,
    sellerQuestionnaireListedFor: doc.sellerQuestionnaireListedFor,
    signers: (doc.signers ?? []).map(questionnaireStepSigner)
  },
  canOpenContacts: false,
  canEdit: false,
  canShare: false,
  canPrintForSigning: false,
  canViewPrepared: doc.hasPreparedPdf,
  previewUrl: null,
  signedPdf: doc.hasSignedPdf ? { href: null } : null,
  questionnairePdf: doc.hasSellerQuestionnaireFile && !isQuestionnaireOnlyDocument(doc) ? { href: null } : null,
  canDownloadAudit: doc.hasAuditCertificate,
  // For a `partial` document the office endpoint composes the same "signed so
  // far" interim the owner gets.
  canDownloadInterim: !doc.hasSignedPdf && doc.status === "partial",
  canArchive: false
});

/** Which of a colleague's files is being fetched. `interim` is the signed endpoint on a partial document. */
type OfficeDownload = "signed" | "interim" | "audit" | "questionnaire" | "prepared";
const officeDownloadFileSuffix: Record<Exclude<OfficeDownload, "prepared">, string> = {
  signed: "",
  interim: "-partial",
  audit: "-audit",
  questionnaire: "-questionnaire"
};

const saveBlobAs = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export default function DocumentDetailPanel({
  documentId,
  onClose,
  onArchived,
  readOnly = false,
  ownerLabel,
  onNotFound,
  setDocuments
}: DocumentDetailPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The caller's OWN document — the only state any mutating handler below
  // reads. It stays null in read-only mode, so those handlers have nothing to
  // act on even if something were to call them.
  const [doc, setDoc] = useState<SignatureDocument | null>(null);
  // Who the signers are in the caller's CRM: one batched lookup of this
  // document's signer phones, not the caller's whole contact book. Built from
  // `doc` alone, which is null in read-only mode — a colleague's signers are
  // named from their document (see `officePanelView`) and the viewer's book
  // is never asked about them.
  const signerPhones = useMemo(() => (doc ? doc.signers.map((s) => s.phone) : []), [doc]);
  const { lookup } = useContactsLookup(signerPhones);
  // A colleague's document (read-only mode), exactly as the office endpoint returned it.
  const [officeDoc, setOfficeDoc] = useState<OrgSignatureDocumentView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  // What the last automatic send said, on the card of the signer it went to: to which number,
  // and that it came from Webot's number — so it is not looked for in the realtor's own WhatsApp.
  const [sentNotice, setSentNotice] = useState<{ signerId: string; text: string } | null>(null);
  // The signer an automatic send is waiting on the realtor's confirmation for: every send asks first,
  // every time (Trello #231 item 7).
  const [confirmSendTo, setConfirmSendTo] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // ContactWidgetDialog handles the contact lookup itself — we just hand it
  // a phone (and a name to seed the "not a contact yet" prompt for unknown
  // signers).
  const [contactDialog, setContactDialog] = useState<{
    phone: string;
    name?: string;
  } | null>(null);
  const [listingModalId, setListingModalId] = useState<string | null>(null);
  const [downloadingInterim, setDownloadingInterim] = useState(false);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [downloadingPrepared, setDownloadingPrepared] = useState(false);
  const [printError, setPrintError] = useState(false);
  const [officeDownloading, setOfficeDownloading] = useState<OfficeDownload | null>(null);
  const [officeDownloadError, setOfficeDownloadError] = useState(false);
  // Read through a ref so a host passing an inline callback does not re-run
  // the loader (and re-fetch the document) on every one of its renders.
  const onNotFoundRef = useRef(onNotFound);
  onNotFoundRef.current = onNotFound;

  const handlePreparedDownload = async () => {
    if (!doc || downloadingPrepared) return;
    setDownloadingPrepared(true);
    setPrintError(false);
    try {
      const blob = await downloadPreparedPdfBlob(doc.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${doc.formattedNumber}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setPrintError(true);
    } finally {
      setDownloadingPrepared(false);
    }
  };

  useEffect(() => {
    if (!documentId) {
      setDoc(null);
      setOfficeDoc(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    setPrintError(false);
    setOfficeDownloadError(false);
    if (readOnly) {
      // A colleague's document: ONE read through the office endpoint. There is
      // no listener to open — the Firestore rule on signature-documents is
      // owner-only and stays that way — so this view is as fresh as the click
      // that opened it. The server answers 404 for a document that is gone,
      // archived, a draft, or whose owner is no longer in the caller's office.
      let cancelled = false;
      setDoc(null);
      setOfficeDoc(null);
      getOrgSignatureDocument(documentId)
        .then((d) => {
          if (cancelled) return;
          setOfficeDoc(d);
          setLoading(false);
        })
        .catch((err: any) => {
          if (cancelled) return;
          // Our own copy for both outcomes — never the transport's message
          // ("Request failed with status code 404") or the server's English string.
          const notFound = err?.response?.status === 404;
          setError(t(notFound ? "signatures.detail.notFound" : "signatures.office.loadError"));
          setLoading(false);
          if (notFound) onNotFoundRef.current?.();
        });
      return () => {
        cancelled = true;
      };
    }
    setOfficeDoc(null);
    // Firestore snapshot keeps the panel live — signer status transitions,
    // WhatsApp delivery updates, and the signed PDF URL arrive as they land
    // on the document without a manual refresh. The REST `getDocument`
    // path was one-shot and silently stale; this replaces it.
    const unsub = subscribeToDocument(
      documentId,
      (d) => {
        setDoc(d);
        setLoading(false);
      },
      (err: any) => {
        setError(err?.message || t("signatures.detail.loadError"));
        setLoading(false);
      }
    );
    return () => unsub();
  }, [documentId, readOnly, t]);

  const handleResend = async (signerId: string) => {
    if (!doc) return;
    setResending(signerId);
    setSentNotice(null);
    try {
      // A contract of a set sends its whole set as ONE message (signingLinkDelivery.ts).
      const { documents, covered } = await sendSigningLinkFor(doc, signerId);
      const updated = documents.find((d) => d.id === doc.id) ?? documents[0];
      setDoc(updated);
      const signer = updated.signers.find((s) => s.id === signerId);
      // A failed WhatsApp keeps its red banner in the delivery box below; only a real 'sent' says so.
      if (signer?.whatsappDelivery?.status === "sent") {
        setSentNotice({ signerId, text: sentConfirmation(t, signer, covered) });
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || t("signatures.detail.resendError"));
    } finally {
      setResending(null);
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
  };

  const handleShare = async () => {
    if (!doc) return;
    const signer = doc.signers[0];
    const url = signer?.signing?.publicSigningUrl;
    if (!url) return;
    const shareTitle = `${doc.formattedNumber} · ${signatureDocumentDisplayName(doc)}`;
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title: shareTitle, url });
        return;
      }
    } catch {
      // Native share cancelled — fall back to clipboard.
    }
    try {
      await navigator.clipboard.writeText(url);
      setToast(t("signatures.share.copied") as string);
      window.setTimeout(() => setToast(null), 2000);
    } catch {
      setToast(t("signatures.share.failed") as string);
      window.setTimeout(() => setToast(null), 2000);
    }
  };

  // Edit-after-send: opens the globally-mounted send dialog in edit mode via
  // URL params (same mechanism as every other "new signature" entry point).
  // Only offered while nobody has signed — see canEditSentDocument.
  const handleEdit = () => {
    if (!doc) return;
    const next = new URLSearchParams(searchParams);
    Object.entries(buildNewSignatureParams({ editDocumentId: doc.id })).forEach(([k, v]) =>
      next.set(k, v)
    );
    setSearchParams(next);
    // Close this detail panel as we open the edit dialog. When the panel is
    // shown as an overlay (DocumentsList / contact & listing tabs) it sits at
    // zIndex 2001 — above the globally-mounted edit dialog (default modal
    // zIndex 1300) — so leaving it open buried the edit form behind the pane.
    // On the standalone detail page the panel is inline content with no
    // onClose, so nothing closes there (and there's no stacking conflict).
    onClose?.();
  };

  const handleArchive = async () => {
    if (!doc) return;
    if (!window.confirm(t("signatures.detail.archiveConfirm"))) return;
    await archiveDocument(doc.id);
    onArchived?.();
    if (onClose) onClose();
    else navigate(PATH_DASHBOARD.signatures.sent);
  };

  // Interim download for a partially-signed document (e.g. hidden-addresses
  // main document signed, addresses addendum still awaiting its signature).
  // Goes through the AUTHENTICATED endpoint (the public stream endpoint only
  // serves completed docs), so we fetch a blob and trigger the save ourselves.
  const handleInterimDownload = async () => {
    if (!doc) return;
    setDownloadingInterim(true);
    try {
      const blob = await downloadSignedPdfBlob(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.formattedNumber}-partial.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || t("signatures.detail.loadError"));
    } finally {
      setDownloadingInterim(false);
    }
  };

  // Download the standalone "Certificate of Electronic Signature". It's served
  // only by the AUTHENTICATED owner endpoint (never bundled into the signed PDF
  // and never exposed on the public signer-facing viewer), so — unlike the
  // signed/questionnaire downloads which are public — we fetch a blob through
  // the token-attaching client and trigger the save ourselves. This keeps the
  // certificate visible to the realtor only, never to the signer.
  const handleDownloadCertificate = async () => {
    if (!doc) return;
    setDownloadingCertificate(true);
    try {
      const blob = await downloadSignedPdfBlob(doc.id, "audit");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.formattedNumber}-audit.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.response?.data?.error || err?.message || t("signatures.detail.loadError"));
    } finally {
      setDownloadingCertificate(false);
    }
  };

  // A colleague's file. Every one of them — signed, interim, certificate,
  // questionnaire — is a blob from the office endpoint, where the server
  // checks the caller's role before it reads Storage. A failure shows inline:
  // the list behind this panel is a one-shot read, so "it was there a minute
  // ago" (archived since, or the role changed) is an ordinary outcome here.
  const handleOfficeDownload = async (kind: Exclude<OfficeDownload, "prepared">) => {
    if (!officeDoc || officeDownloading) return;
    setOfficeDownloading(kind);
    setOfficeDownloadError(false);
    try {
      const blob = await downloadOrgSignedPdfBlob(
        officeDoc.id,
        kind === "interim" ? "signed" : kind
      );
      saveBlobAs(blob, `${officeDoc.formattedNumber}${officeDownloadFileSuffix[kind]}.pdf`);
    } catch {
      setOfficeDownloadError(true);
    } finally {
      setOfficeDownloading(null);
    }
  };

  // "צפייה במסמך שנשלח" for a colleague's document: the PDF as it was sent,
  // opened in a new tab to LOOK at. Deliberately not the owner's "print for
  // signing" — getting a wet signature on a colleague's contract is not an
  // office admin's action.
  const handleOfficeViewPrepared = async () => {
    if (!officeDoc || officeDownloading) return;
    // The tab is opened inside the click, before the fetch: one opened after
    // the await is outside the user gesture and Safari / mobile Chrome drop it.
    const tab = window.open("", "_blank");
    setOfficeDownloading("prepared");
    setOfficeDownloadError(false);
    try {
      const blob = await downloadOrgPreparedPdfBlob(officeDoc.id);
      if (tab) {
        const url = URL.createObjectURL(blob);
        tab.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        // Popup blocked — the file is still the admin's to see.
        saveBlobAs(blob, `${officeDoc.formattedNumber}.pdf`);
      }
    } catch {
      tab?.close();
      setOfficeDownloadError(true);
    } finally {
      setOfficeDownloading(null);
    }
  };

  // Everything rendered below comes from this view-model — see the note above
  // PanelDocumentView. Resolved up-front so the actions row can be skipped
  // entirely (rather than rendering an empty margin-bearing strip) for
  // documents that offer none — e.g. a draft with no signing links and no
  // rendered PDF yet.
  const view: PanelDocumentView | null = readOnly
    ? officeDoc && officePanelView(officeDoc)
    : doc && ownerPanelView(doc, lookup);
  const taskLabelContext = (task: PostSignTask): "rent" | undefined =>
    task === "questionnaire" ? questionnaireLabelContext(view?.questionnaireListedFor) : undefined;
  const hasHeaderActions =
    !!view &&
    (view.canEdit ||
      view.canPrintForSigning ||
      view.canViewPrepared ||
      !!view.previewUrl ||
      view.canShare);
  // The two blob downloads both modes offer differ only in their transport.
  const downloadInterim = readOnly ? () => handleOfficeDownload("interim") : handleInterimDownload;
  const interimBusy = readOnly ? officeDownloading === "interim" : downloadingInterim;
  const downloadCertificate = readOnly
    ? () => handleOfficeDownload("audit")
    : handleDownloadCertificate;
  const certificateBusy = readOnly ? officeDownloading === "audit" : downloadingCertificate;
  // A colleague's files are fetched one at a time (both office handlers return
  // while one is in flight), so every office download button is disabled for
  // that long — an enabled button that swallows its click reads as broken. The
  // spinner stays on the one in flight. Always false in owner mode.
  const officeBusy = officeDownloading !== null;
  const agentName = ownerLabel?.trim() || officeDoc?.ownerUserId || "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header: title on its own line, actions on a second line beneath it.
          The actions used to be a row of bare icons crowded against the close
          button in the title bar, which made "edit" effectively undiscoverable
          — realtors couldn't find it. They're now labelled buttons on a
          dedicated row, with edit contained (the primary thing you'd come here
          to do) and the rest outlined. */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {view
                ? `${view.formattedNumber} · ${view.displayName}`
                : t("signatures.detail.headerFallback")}
            </Typography>
          </Box>
          {onClose && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
        {view && hasHeaderActions && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
          >
            {view.canEdit && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon fontSize="small" />}
                onClick={handleEdit}
              >
                {t("signatures.actions.editShort")}
              </Button>
            )}
            {view.canPrintForSigning && (
              <Button size="small" variant="outlined" disabled={downloadingPrepared}
                startIcon={downloadingPrepared ? <CircularProgress size={14} /> : <PrintIcon fontSize="small" />}
                onClick={handlePreparedDownload}>
                {t("signatures.detail.printForSigning")}
              </Button>
            )}
            {view.canViewPrepared && (
              <Button
                size="small"
                variant="outlined"
                disabled={officeBusy}
                startIcon={
                  officeDownloading === "prepared" ? (
                    <CircularProgress size={14} />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )
                }
                onClick={handleOfficeViewPrepared}
              >
                {t("signatures.office.viewSentDocument")}
              </Button>
            )}
            {view.previewUrl && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon fontSize="small" />}
                onClick={() => setPreviewOpen(true)}
              >
                {t("signatures.actions.previewShort")}
              </Button>
            )}
            {/* Share vanishes once the primary signer has signed (or the
                document is declined/expired) — the signing link is spent and
                handing it out again only confuses the signer. There is no
                WhatsApp action up here: re-sending is a PER-SIGNER operation
                and lives on each signer's card below, where it's unambiguous
                who the message goes to. */}
            {view.canShare && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ShareIcon fontSize="small" />}
                onClick={handleShare}
              >
                {t("signatures.actions.shareShort")}
              </Button>
            )}
          </Stack>
        )}
        {/* A failed office download says so HERE, in the header, which does not
            scroll. The buttons that fail are spread over the whole panel — "view
            sent document" just above, the signed PDF, questionnaire, certificate
            and interim at the very bottom of the body — and an alert at the top
            of the scrolling body was out of view of the button that was clicked:
            the spinner stopped and nothing else changed on screen. */}
        {officeDownloadError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {t("signatures.office.downloadError")}
          </Alert>
        )}
      </Box>

      <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {printError && <Alert severity="error" sx={{ mb: 2 }}>{t("signatures.prepared.downloadError")}</Alert>}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !view ? (
          <Alert severity="error">{error || t("signatures.detail.notFound")}</Alert>
        ) : (
          <Stack spacing={2}>
            {/* Whose document this is, and that nothing here can change it —
                said first, before the admin looks for a button that is not there. */}
            {readOnly && (
              <Alert severity="info">
                {t("signatures.office.readOnlyNotice", { name: agentName })}
              </Alert>
            )}

            {/* Status + key timestamps — the realtor's at-a-glance summary. */}
            <Card sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2">{t("signatures.detail.statusTitle")}</Typography>
                {/* The status, and the questionnaire's step beside it (the lists' own chip). */}
                <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ gap: 0.5, flexWrap: "wrap" }}>
                  <Label color={documentStatusColor(view.displayStatus)} variant="ghost">
                    {t(`signatures.status.${view.displayStatus}`)}
                  </Label>
                  <QuestionnaireStepLabel doc={view.questionnaireStep} />
                </Stack>
              </Stack>
              <Stack spacing={0.75}>
                {readOnly && (
                  <MetaRow label={t("signatures.detail.metaAgent")} value={agentName} />
                )}
                <MetaRow label={t("signatures.detail.metaCreated")} value={formatDateTime(view.createdTime)} />
                <MetaRow label={t("signatures.detail.metaSentAt")} value={formatDateTime(view.sentAt)} />
                {view.completedAt && (
                  <MetaRow
                    label={t("signatures.detail.metaCompletedAt")}
                    value={formatDateTime(view.completedAt)}
                  />
                )}
                {view.updatedTime && (
                  <MetaRow
                    label={t("signatures.detail.metaUpdated")}
                    value={formatDateTime(view.updatedTime)}
                  />
                )}
                <MetaRow label={t("signatures.detail.metaDocNumber")} value={view.formattedNumber} />
                <MetaRow
                  label={t("signatures.detail.metaTemplate")}
                  value={view.templateNameSnapshot}
                />
                {view.listingIds.length > 0 && (
                  <MetaRow
                    label={t("signatures.detail.metaListings")}
                    value={t("signatures.card.countsListings", { count: view.listingIds.length })}
                  />
                )}
                {view.contactIds.length > 0 && (
                  <MetaRow
                    label={t("signatures.detail.metaContacts")}
                    value={t("signatures.card.countsSigners", { count: view.contactIds.length })}
                  />
                )}
              </Stack>
            </Card>

            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("signatures.detail.signersTitle")}
              </Typography>
              <Stack spacing={2} divider={<Divider />}>
                {view.signers.map((s) => (
                  <Stack key={s.id} spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      {/* Clickable signer block — opens the contact modal.
                          Hover border mirrors the pattern on the sent-list
                          cards so the realtor gets the same "this is
                          clickable" affordance in both places. On a
                          colleague's document the block is inert: the contact
                          modal searches the VIEWER's contacts and would offer
                          to add the colleague's customer as their own. */}
                      <Box
                        {...(view.canOpenContacts && {
                          role: "button",
                          tabIndex: 0,
                          onClick: () => {
                            if (s.contact) setContactDialog(s.contact);
                          }
                        })}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          px: 0.75,
                          py: 0.5,
                          borderRadius: 1,
                          border: "1px solid transparent",
                          ...(view.canOpenContacts && {
                            cursor: "pointer",
                            transition:
                              "border-color 0.15s ease, background-color 0.15s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              bgcolor: "primary.lighter"
                            }
                          })
                        }}
                      >
                        <Typography variant="subtitle2">{s.displayName}</Typography>
                        {s.displayPhone && (
                          <Typography variant="caption" color="text.secondary">
                            {s.displayPhone}
                          </Typography>
                        )}
                        {s.email && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {s.email}
                          </Typography>
                        )}
                      </Box>
                      <Label color={signerStatusColor(s.status)} variant="ghost">
                        {t(`signatures.signerStatus.${s.status}`)}
                      </Label>
                    </Stack>

                    {/* Signer-status timeline — surface every timestamp the
                        backend populates on this signer so the realtor can
                        answer "what's happened to this signer" without
                        leaving the panel. */}
                    <Stack spacing={0.5}>
                      {s.sentAt && (
                        <MetaRow
                          label={t("signatures.detail.signerSentAt")}
                          value={formatDateTime(s.sentAt)}
                        />
                      )}
                      {s.viewedAt && (
                        <MetaRow
                          label={t("signatures.detail.signerViewedAt")}
                          value={formatDateTime(s.viewedAt)}
                        />
                      )}
                      {/* Multi-phase ceremony milestone — the moment
                          mainSignedAt lands, the main document is signed
                          (and, on hidden-addresses docs, the full addresses
                          were revealed). The per-task completion state lives
                          in the tasks block below. */}
                      {s.mainSignedAt && (
                        <MetaRow
                          label={t("signatures.detail.signerMainSignedAt")}
                          value={formatDateTime(s.mainSignedAt)}
                        />
                      )}
                      {s.signedAt && (
                        <MetaRow
                          label={t("signatures.detail.signerSignedAt")}
                          value={formatDateTime(s.signedAt)}
                        />
                      )}
                      {s.declinedAt && (
                        <MetaRow
                          label={t("signatures.detail.signerDeclinedAt")}
                          value={formatDateTime(s.declinedAt)}
                        />
                      )}
                    </Stack>

                    {/* OTP possession proof — the audit certificate's trust
                        line surfaced in the panel: verified (with timestamp),
                        explicitly waived by the sender at send time, or not
                        verified yet. */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Label
                        color={
                          s.otpVerifiedAt
                            ? "success"
                            : s.otpSkippedInPerson || view.otpRequired === false
                            ? "warning"
                            : "default"
                        }
                        variant="ghost"
                      >
                        {s.otpVerifiedAt
                          ? t("signatures.detail.otpVerified")
                          : s.otpSkippedInPerson
                          ? t("signatures.detail.otpSkippedInPerson")
                          : view.otpRequired === false
                          ? t("signatures.detail.otpWaived")
                          : t("signatures.detail.otpNotVerified")}
                      </Label>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {t("signatures.detail.otpTitle")}
                      </Typography>
                      {(s.otpVerifiedAt || s.otpSkippedInPerson) && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(s.otpVerifiedAt || s.otpSkippedInPersonAt)}
                        </Typography>
                      )}
                    </Stack>

                    {/* Post-signing tasks — one clear status row per task the
                        document carries (addresses confirmation / seller
                        questionnaire), per signer: completed (with date),
                        awaiting the signer (main already signed), or not yet
                        reached (main not signed). The questionnaire's row reads
                        the lists' own rule (`signerQuestionnaireStep`: reached
                        once the contract AND its addresses are signed) in the
                        lists' own words and colours. */}
                    {view.postSignTasks.length > 0 && (
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 0.5 }}
                        >
                          {t("signatures.detail.tasksTitle")}
                        </Typography>
                        <Stack spacing={0.75}>
                          {view.postSignTasks.map((task) => {
                            const doneAt =
                              task === "addendum" ? s.addendumSignedAt : s.questionnaireSignedAt;
                            const awaitingSigner = !doneAt && !!s.mainSignedAt;
                            const questionnaireStep =
                              task === "questionnaire"
                                ? signerQuestionnaireStep(s, view.postSignTasks.includes("addendum"))
                                : null;
                            return (
                              <Stack key={task} direction="row" alignItems="center" spacing={1}>
                                <Label
                                  color={
                                    questionnaireStep
                                      ? QUESTIONNAIRE_STEP_COLOR[questionnaireStep]
                                      : doneAt
                                      ? "success"
                                      : awaitingSigner
                                      ? "warning"
                                      : "default"
                                  }
                                  variant="ghost"
                                >
                                  {questionnaireStep
                                    ? t(`signatures.questionnaireStep.task.${questionnaireStep}`)
                                    : doneAt
                                    ? t("signatures.detail.taskDone")
                                    : awaitingSigner
                                    ? t("signatures.detail.taskPending")
                                    : t("signatures.detail.taskNotStarted")}
                                </Label>
                                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                  {t(postSignTaskLabelKey[task], { context: taskLabelContext(task) })}
                                </Typography>
                                {doneAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(doneAt)}
                                  </Typography>
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    {/* WhatsApp delivery summary — attempts, last attempt,
                        any error the backend captured. Always shown (even
                        on success) so the realtor knows the document made
                        it to the signer. */}
                    <Box
                      sx={{
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                        <Label color={whatsappStatusColor(s.whatsapp.status)} variant="ghost">
                          {t(`signatures.detail.whatsappStatus.${s.whatsapp.status}`)}
                        </Label>
                      </Stack>
                      <Stack spacing={0.25}>
                        <MetaRow
                          label={t("signatures.detail.whatsappAttempts")}
                          value={String(s.whatsapp.attemptCount)}
                        />
                        {s.whatsapp.lastAttemptAt && (
                          <MetaRow
                            label={t("signatures.detail.whatsappLastAttempt")}
                            value={formatDateTime(s.whatsapp.lastAttemptAt)}
                          />
                        )}
                        {s.whatsapp.messageId && (
                          <MetaRow
                            label={t("signatures.detail.whatsappMessageId")}
                            value={s.whatsapp.messageId}
                            mono
                          />
                        )}
                      </Stack>
                      {s.whatsapp.status === "failed" && s.whatsapp.error && (
                        <Alert severity="error" sx={{ mt: 1, py: 0.25 }}>
                          {s.whatsapp.error}
                        </Alert>
                      )}
                      {s.whatsapp.attemptCount > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {t("signatures.delivery.fromWebot")}
                        </Typography>
                      )}
                    </Box>

                    {s.signingUrl && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ bgcolor: "grey.100", borderRadius: 1, p: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: "monospace"
                          }}
                        >
                          {s.signingUrl}
                        </Typography>
                        <IconButton size="small" onClick={() => handleCopy(s.signingUrl!)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          component="a"
                          href={s.signingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}

                    {s.canSignInPerson && s.signingUrl && (
                      <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                        component="a" href={s.signingUrl} target="_blank" rel="noopener noreferrer">
                        {t("signatures.prepared.signInPerson")}
                      </Button>
                    )}
                    {s.canResend && (
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={
                          resending === s.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <ForwardSendIcon fontSize="small" />
                          )
                        }
                        onClick={() => setConfirmSendTo(s.id)}
                        disabled={resending === s.id}
                      >
                        {t("signatures.detail.resendWhatsapp")}
                      </Button>
                    )}
                    {s.canShareOnWhatsapp && doc && (
                      <SigningLinkWhatsappShareButton documents={[doc]} signerId={s.id} size="small" />
                    )}
                    {sentNotice?.signerId === s.id && (
                      <Alert
                        severity="success"
                        sx={{ py: 0.25 }}
                        closeText={t("signatures.close") as string}
                        onClose={() => setSentNotice(null)}
                      >
                        {sentNotice.text}
                      </Alert>
                    )}
                  </Stack>
                ))}
              </Stack>
            </Card>

            {/* Listings attached to this document — same chip style as on
                the sent-list cards so the realtor has one consistent visual
                for a listing reference. Clicking a chip opens the listing
                details modal. */}
            {view.listingIds.length > 0 && (
              <Card sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("signatures.detail.listingsTitle")}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  {view.listingIds.map((lid) => (
                    <ListingChip
                      key={lid}
                      listingId={lid}
                      onClick={(id) => setListingModalId(id)}
                    />
                  ))}
                </Stack>
              </Card>
            )}

            {view.signedPdf &&
              (view.signedPdf.href !== null ? (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={view.signedPdf.href}
                  target="_blank"
                  rel="noopener"
                  size="large"
                >
                  {view.questionnaireOnly
                    ? t("signatures.detail.downloadSignedQuestionnairePdf", {
                        context: questionnaireLabelContext(view.questionnaireListedFor)
                      })
                    : t("signatures.detail.downloadSignedPdf")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={
                    officeDownloading === "signed" ? <CircularProgress size={16} /> : <DownloadIcon />
                  }
                  onClick={() => handleOfficeDownload("signed")}
                  disabled={officeBusy}
                  size="large"
                >
                  {view.questionnaireOnly
                    ? t("signatures.detail.downloadSignedQuestionnairePdf", {
                        context: questionnaireLabelContext(view.questionnaireListedFor)
                      })
                    : t("signatures.detail.downloadSignedPdf")}
                </Button>
              ))}

            {/* The seller questionnaire is a standalone statutory PDF —
                composed + stored separately from the signed contract. */}
            {view.questionnairePdf &&
              (view.questionnairePdf.href !== null ? (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={view.questionnairePdf.href}
                  target="_blank"
                  rel="noopener"
                  size="large"
                >
                  {t("signatures.detail.downloadQuestionnairePdf", {
                    context: questionnaireLabelContext(view.questionnaireListedFor)
                  })}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={
                    officeDownloading === "questionnaire" ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  onClick={() => handleOfficeDownload("questionnaire")}
                  disabled={officeBusy}
                  size="large"
                >
                  {t("signatures.detail.downloadQuestionnairePdf", {
                    context: questionnaireLabelContext(view.questionnaireListedFor)
                  })}
                </Button>
              ))}

            {/* Certificate of Electronic Signature — a discreet text link UNDER
                the main PDF downloads. Realtor-only: it is never part of the
                signed PDF and never reaches the signer's viewer, so the realtor
                gets it here separately. Fetched via the authenticated endpoint
                (blob), not a plain href, so the Firebase token is attached. */}
            {view.canDownloadAudit && (
              <Box>
                <Button
                  variant="text"
                  size="small"
                  startIcon={
                    certificateBusy ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />
                  }
                  onClick={downloadCertificate}
                  disabled={certificateBusy || officeBusy}
                  sx={{ color: "text.secondary" }}
                >
                  {t("signatures.detail.downloadCertificate")}
                </Button>
              </Box>
            )}

            {/* Interim download for a partially-signed document: the main
                signature is already on record even though the ceremony isn't
                complete (hidden-addresses addendum pending, or a multi-signer
                doc mid-flight). Composed on demand server-side — main pages
                only, no addendum, no audit certificate. */}
            {view.canDownloadInterim && (
              <Button
                variant="outlined"
                startIcon={interimBusy ? <CircularProgress size={16} /> : <DownloadIcon />}
                onClick={downloadInterim}
                disabled={interimBusy || officeBusy}
                size="large"
                sx={{
                  color: "warning.dark",
                  borderColor: "warning.main",
                  "&:hover": { borderColor: "warning.dark", bgcolor: "rgba(255,193,7,0.08)" }
                }}
              >
                {view.postSignTasks.length > 0
                  ? t("signatures.detail.downloadMainSignedPdfWithout", {
                      tasks: view.postSignTasks
                        .map((task) => t(postSignTaskLabelKey[task], { context: taskLabelContext(task) }))
                        .join(" + ")
                    })
                  : t("signatures.detail.downloadPartialSignedPdf")}
              </Button>
            )}

            {view.notes && (
              <Card sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  {t("signatures.detail.notesTitle")}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {view.notes}
                </Typography>
              </Card>
            )}

            {/* Debug / admin info at the bottom — useful when the realtor
                calls support and needs to share the underlying identifiers. */}
            <Card sx={{ p: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                {t("signatures.detail.identifiersTitle")}
              </Typography>
              <Stack spacing={0.25}>
                <MetaRow label={t("signatures.detail.idDocument")} value={view.id} mono />
                <MetaRow label={t("signatures.detail.idTemplate")} value={view.templateId} mono />
              </Stack>
            </Card>

            {view.canArchive && (
              <Box>
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleArchive}
                  size="small"
                >
                  {t("signatures.detail.archive")}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Every dialog opened FROM this panel has to clear it: on the documents
          list and the contact/listing signature tabs the panel is a hand-rolled
          fixed overlay at zIndex 2001, so a default-stacked MUI modal (1300)
          opens *behind* it — which is exactly how the contact view ended up
          buried. PANEL_DIALOG_Z_INDEX is the shared level, matching the one
          ListingPreviewModal already hard-codes for the same reason.

          Neither of these two exists for a colleague's document: there is no
          inline preview (its files are fetched, not linked) and no contact to
          open. */}
      {!readOnly && (
        <>
          <DocumentPreviewDialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            url={view?.previewUrl ?? null}
            title={view ? `${view.formattedNumber} · ${view.displayName}` : undefined}
            zIndex={PANEL_DIALOG_Z_INDEX}
          />

          <ContactWidgetDialog
            open={!!contactDialog}
            onClose={() => setContactDialog(null)}
            phone={contactDialog?.phone ?? ""}
            nameFallback={contactDialog?.name}
            zIndex={PANEL_DIALOG_Z_INDEX}
          />

          {(() => {
            const signer = confirmSendTo ? doc?.signers.find((item) => item.id === confirmSendTo) : undefined;
            return (
              <WhatsappConfirmDialog
                open={!!signer}
                signerName={signer?.name}
                signerPhone={signer ? deliveryRecipientLabel({ phone: signer.phone }) : undefined}
                inSet={!!doc?.bundleId}
                questionnaire={doc && signer ? questionnaireInMessage(doc, setDocuments) : null}
                zIndex={PANEL_DIALOG_Z_INDEX}
                onCancel={() => setConfirmSendTo(null)}
                onConfirm={async () => {
                  if (!signer) return;
                  await handleResend(signer.id);
                  setConfirmSendTo(null);
                }}
              />
            );
          })()}
        </>
      )}

      <ListingPreviewModal
        open={!!listingModalId}
        listingId={listingModalId}
        onClose={() => setListingModalId(null)}
      />

      {toast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            bgcolor: "text.primary",
            color: "background.paper",
            px: 2,
            py: 1,
            borderRadius: 1,
            boxShadow: 3,
            zIndex: 1500,
            fontSize: 14
          }}
        >
          {toast}
        </Box>
      )}
    </Box>
  );
}
